import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { MatchKind } from "@core/enums/match-kind";
import { BankBatch } from "@core/models/bank-batch-model";
import { BankCollectionBatch } from "@core/models/bank-collection-batch";
import { BankTransactionMatch } from "@core/models/bank-transaction-match.model";
import { CollectionTransaction } from "@core/models/collection-transaction.model";
import { DisbursementTransactionAction } from "@core/models/disbursement-transaction-action.model";
import { ExtendedGfhAccount } from "@core/models/extended-gfh-account.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { PendingTransaction } from "@core/models/pending-transaction.model";
import { Search } from "@core/models/search.model";
import { Transaction } from "@core/models/transaction.model";
import { TableSorting } from "@custom-types/sorting";
import { environment } from "@env/environment";
import { SortingParams } from "@features/+shared/sorting-params/sorting-params";
import { map, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class TransactionService {
  constructor(private http: HttpClient) {}

  getBankBatchList$(): Observable<Array<BankBatch>> {
    return this.http
      .get<Array<BankBatch>>(`${environment.BASE_URL}bankbatch`)
      .pipe(
        map((response: Array<BankBatch>): Array<BankBatch> => {
          return response;
        })
      );
  }

  getFundList$(): Observable<Array<FundWithRelated>> {
    return this.http
      .get<
        Array<FundWithRelated>
      >(`${environment.BASE_URL}fund/latestnav`, { withCredentials: true })
      .pipe(
        map((response: Array<FundWithRelated>): Array<FundWithRelated> => {
          return response;
        })
      );
  }

  getTransactionList$(searchItems: Search): Observable<Array<Transaction>> {
    let params = new HttpParams({});
    for (const [key, value] of Object.entries(searchItems)) {
      params = params.append(key, value);
    }
    return this.http
      .get<
        Array<Transaction>
      >(`${environment.BASE_URL}banktransactions/`, { params })
      .pipe(
        map((response: Array<Transaction>): Array<Transaction> => {
          return response;
        })
      );
  }

  uploadCustomerStatement$(document: File): Observable<unknown> {
    const formData = new FormData();
    formData.append("file", document, document.name);
    return this.http.post(`${environment.BASE_URL}bankbatch/`, formData);
  }

  revertTransaction$(id: string): Observable<unknown> {
    return this.http.delete(
      `${environment.BASE_URL}banktransactionmatches/${id}`
    );
  }

  suppressTransaction$(
    id: string,
    suppressed: boolean,
    version: string
  ): Observable<unknown> {
    return this.http.patch(`${environment.BASE_URL}banktransactions/${id}`, {
      suppressed,
      version,
    });
  }

  getGfhAccountList$(
    idList?: Array<string>,
    searchTerm?: string
  ): Observable<Array<ExtendedGfhAccount>> {
    if (searchTerm) {
      let params = new HttpParams({});
      params = params.append("searchTerm", searchTerm);
      return this.http
        .get<
          Array<ExtendedGfhAccount>
        >(`${environment.BASE_URL}gfhaccount`, { params })
        .pipe(
          map(
            (
              response: Array<ExtendedGfhAccount>
            ): Array<ExtendedGfhAccount> => {
              return response;
            }
          )
        );
    }
    return this.http
      .post<
        Array<ExtendedGfhAccount>
      >(`${environment.BASE_URL}gfhaccount/batchgetbyid`, idList)
      .pipe(
        map(
          (response: Array<ExtendedGfhAccount>): Array<ExtendedGfhAccount> => {
            return response;
          }
        )
      );
  }

  saveTransactionMatch$(
    bankTransactionMatch: BankTransactionMatch,
    matchId: string
  ): Observable<unknown> {
    if (bankTransactionMatch.matchKind !== MatchKind.FailedCollection) {
      return this.http.patch(
        `${environment.BASE_URL}banktransactionmatches/${matchId}`,
        bankTransactionMatch
      );
    }
    return this.http.post(
      `${environment.BASE_URL}banktransactionmatches/`,
      bankTransactionMatch
    );
  }

  getBankCollectionBatchList$(
    sorting?: TableSorting
  ): Observable<Array<BankCollectionBatch>> {
    const params = SortingParams.appendParams(sorting);
    return this.http
      .get<
        Array<BankCollectionBatch>
      >(`${environment.BASE_URL}collectionbatch`, { params })
      .pipe(
        map(
          (
            response: Array<BankCollectionBatch>
          ): Array<BankCollectionBatch> => {
            return response;
          }
        )
      );
  }

  getCollection$(id: string): Observable<HttpResponse<ArrayBuffer>> {
    return this.http.get(
      `${environment.BASE_URL}collectionbatch/${id}/download`,
      {
        responseType: "arraybuffer",
        observe: "response",
      }
    );
  }

  getBankRetryCollectionBatchList$(
    sorting?: TableSorting
  ): Observable<Array<BankCollectionBatch>> {
    const params = SortingParams.appendParams(sorting);
    return this.http
      .get<
        Array<BankCollectionBatch>
      >(`${environment.BASE_URL}collectionbatch/failed`, { params })
      .pipe(
        map(
          (
            response: Array<BankCollectionBatch>
          ): Array<BankCollectionBatch> => {
            return response;
          }
        )
      );
  }

  getBankFailedCollectionBatchList$(
    sorting?: TableSorting
  ): Observable<Array<CollectionTransaction>> {
    const params = SortingParams.appendParams(sorting);
    return this.http
      .get<
        Array<CollectionTransaction>
      >(`${environment.BASE_URL}collectiontransaction/failed`, { params })
      .pipe(
        map(
          (
            response: Array<CollectionTransaction>
          ): Array<CollectionTransaction> => {
            return response;
          }
        )
      );
  }

  exportPeriodicCollection$(): Observable<unknown> {
    return this.http.post(`${environment.BASE_URL}collectionbatch/`, undefined);
  }

  exportFailedCollection$(
    collectionsToRetry: Array<string>
  ): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}collectionbatch/retry`,
      collectionsToRetry
    );
  }

  suppressCollectionTransaction$(collectionInformation: {
    id: string;
    version: string;
  }): Observable<unknown> {
    return this.http.patch(
      `${environment.BASE_URL}collectiontransaction/${collectionInformation.id}/suppress`,
      { version: collectionInformation.version }
    );
  }

  makeCollectionsDefinitive$(): Observable<unknown> {
    return this.http.patch(
      `${environment.BASE_URL}collectionbatch/`,
      undefined
    );
  }

  getDisbursementList$(
    sorting?: TableSorting
  ): Observable<Array<PendingTransaction>> {
    const params = SortingParams.appendParams(sorting);

    return this.http
      .get<
        Array<PendingTransaction>
      >(`${environment.BASE_URL}disbursement`, { params })
      .pipe(
        map(
          (response: Array<PendingTransaction>): Array<PendingTransaction> => {
            return response;
          }
        )
      );
  }

  createDisbursementBatch$(
    disbursementTransactionActionList: Array<DisbursementTransactionAction>
  ): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}disbursement`,
      disbursementTransactionActionList
    );
  }

  createDisbursementReport$(): Observable<unknown> {
    return this.http.get(`${environment.BASE_URL}report/disbursementReport`);
  }

  processDisbursement$(
    disbursementInformation: DisbursementTransactionAction
  ): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}disbursement/individual`,
      disbursementInformation
    );
  }
}
